import { useColorScheme, useTheme } from "@mui/material";
import * as monaco from "monaco-editor";
import { useEffect, useMemo, useRef, useState } from "react";
import { getSbsTheme } from "../components/MonacoEditor/utils";
import { KEYWORDS, TYPES } from "../lib/SBSEditorConfig/constants";

type EditorOptions = {
  value?: string;
  options: monaco.editor.IEditorOptions;
};

function useEditor({ value, options = {} }: EditorOptions) {
  const defaultValue = "// An empty script.";
  const editorRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null);
  const monacoEl = useRef<HTMLDivElement | null>(null);
  const [isEditorReady, setIsEditorReady] = useState<boolean>(false);
  const { mode, systemMode } = useColorScheme();
  const theme = useTheme();

  const vsTheme = mode === "dark" || systemMode === "dark" ? "vs-dark" : "vs";

  useEffect(() => {
    monaco.editor.setTheme(vsTheme);
  }, [vsTheme]);

  const disposeEditor = () => {
    const model = editorRef.current?.getModel();
    model?.dispose();

    editorRef.current?.dispose();
  };

  useEffect(() => {
    if (monacoEl && monacoEl.current) {
      // Apply SBS config
      monaco.languages.register({ id: "sbs" });

      monaco.languages.setMonarchTokensProvider("sbs", {
        keywords: [...KEYWORDS, "import", "export"],
        types: TYPES,
        tokenizer: {
          root: [
            [
              /@?[a-zA-Z][\w$]*/,
              {
                cases: {
                  "@keywords": "keyword",
                  "@types": "type",
                  "@default": "variable",
                },
              },
            ],
            [/".*?"/, "string"],
            [/\/\*/, "comment", "@comment"], // Multi-line comments start
            [/\/\/.*/, "comment"], // Single-line comments start
          ],
          comment: [
            [/\*\//, "comment", "@pop"],
            [/[^*]+/, "comment"],
            [/\*/, "comment"],
            [/\/\*/, "comment", "@comment"], // Nested multi-line comments start
            [/\/\*/, "comment.invalid"], // Malformed multi-line comments
            [/\/\/.*/, "comment"], // Single-line comments can follow multi-line
            [/.*\*\//, "comment", "@comment"],
          ],
        },
      });

      monaco.editor.defineTheme("sbs-theme", getSbsTheme(theme, vsTheme));

      editorRef.current = monaco.editor.create(monacoEl.current, {
        value: value || defaultValue,
        language: "sbs",
        theme: "sbs-theme",
        automaticLayout: true,
        glyphMargin: true,
        codeLens: false,
        // See https://github.com/microsoft/monaco-editor/issues/5059
        editContext: typeof window !== "undefined" && window.Cypress ? false : undefined,
        ...options,
      });

      setIsEditorReady(true);
    }

    return () => disposeEditor();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- this is subtle to fix
  }, []);

  return useMemo(
    () => ({
      editorRef,
      monacoEl,
      isEditorReady,
    }),
    [editorRef, monacoEl, isEditorReady]
  );
}

export default useEditor;
